import { ApiError, apiCommand, apiRequest, BASE_URL } from './client';
import { clearToken, getToken, setToken } from './token';
import type { CaseStudy, Insight, Lead, ServiceArea } from '../types/content';

/** Ligger under samma prefix som resten av API:et (Azure Functions rutt-prefix). */
const SESSION_URL = `${BASE_URL}/admin/session`;

/**
 * Loggar in och sparar den token servern svarar med. Resten av admin-anropen
 * plockar upp den automatiskt i API-klienten.
 */
export async function login(username: string, password: string): Promise<void> {
  const response = await fetch(SESSION_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ username, password })
  });

  if (!response.ok) {
    throw new ApiError('Fel användarnamn eller lösenord.', response.status);
  }

  const { token } = (await response.json()) as { token: string };
  setToken(token);
}

export function fetchSession(): Promise<{ username: string }> {
  // Utan token är svaret givet – hoppa över anropet så att admin visar
  // inloggningen direkt vid en vanlig sidladdning.
  if (!getToken()) {
    return Promise.reject(new ApiError('Ingen inloggning.', 401));
  }
  return apiRequest<{ username: string }>('/admin/session');
}

/** Återkallar sessionen på servern. Lokalt släpps den oavsett hur det går. */
export async function logout(): Promise<void> {
  try {
    await apiCommand('/admin/session', { method: 'DELETE' });
  } catch {
    /* Servern kan vara onåbar – då räcker det att slänga token här. */
  } finally {
    clearToken();
  }
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };

function write<T>(path: string, method: 'POST' | 'PUT', payload: unknown): Promise<T> {
  return apiRequest<T>(path, { method, headers: JSON_HEADERS, body: JSON.stringify(payload) });
}

export const adminServices = {
  list: () => apiRequest<ServiceArea[]>('/admin/services'),
  create: (service: ServiceArea) => write<ServiceArea>('/admin/services', 'POST', service),
  update: (id: string, service: ServiceArea) =>
    write<ServiceArea>(`/admin/services/${encodeURIComponent(id)}`, 'PUT', service),
  remove: (id: string) =>
    apiCommand(`/admin/services/${encodeURIComponent(id)}`, { method: 'DELETE' })
};

export const adminCases = {
  list: () => apiRequest<CaseStudy[]>('/admin/cases'),
  create: (item: CaseStudy) => write<CaseStudy>('/admin/cases', 'POST', item),
  update: (id: string, item: CaseStudy) =>
    write<CaseStudy>(`/admin/cases/${encodeURIComponent(id)}`, 'PUT', item),
  remove: (id: string) => apiCommand(`/admin/cases/${encodeURIComponent(id)}`, { method: 'DELETE' })
};

export const adminInsights = {
  list: () => apiRequest<Insight[]>('/admin/insights'),
  create: (item: Insight) => write<Insight>('/admin/insights', 'POST', item),
  update: (slug: string, item: Insight) =>
    write<Insight>(`/admin/insights/${encodeURIComponent(slug)}`, 'PUT', item),
  remove: (slug: string) =>
    apiCommand(`/admin/insights/${encodeURIComponent(slug)}`, { method: 'DELETE' })
};

export const adminLeads = {
  list: () => apiRequest<Lead[]>('/admin/leads')
};
